import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  FileText, RefreshCw, Plus, Clock, CheckCircle, XCircle, Users,
  Building, Phone, Calendar, ChevronRight, Send, Eye, TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralSourcePortal = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showNewReferral, setShowNewReferral] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [newReferral, setNewReferral] = useState({
    patient_name: '', patient_dob: '', patient_phone: '', patient_address: '',
    diagnosis: '', diagnosis_details: '', prognosis: '',
    referral_source: '', referring_physician: '', physician_phone: '', physician_npi: '',
    insurance_type: '', insurance_id: '',
    urgency: 'routine', notes: '',
    contact_name: '', contact_phone: '', contact_relationship: ''
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase68/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDashboardData(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferralDetail = async (referralId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase68/referrals/${referralId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedReferral(data);
      }
    } catch (error) {
      toast.error('Failed to load referral details');
    }
  };

  const submitReferral = async () => {
    if (!newReferral.patient_name || !newReferral.diagnosis || !newReferral.referring_physician) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/phase68/referrals`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newReferral)
      });
      if (response.ok) {
        toast.success('Referral submitted successfully');
        setShowNewReferral(false);
        setNewReferral({
          patient_name: '', patient_dob: '', patient_phone: '', patient_address: '',
          diagnosis: '', diagnosis_details: '', prognosis: '',
          referral_source: '', referring_physician: '', physician_phone: '', physician_npi: '',
          insurance_type: '', insurance_id: '', urgency: 'routine', notes: '',
          contact_name: '', contact_phone: '', contact_relationship: ''
        });
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to submit referral');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: 'bg-yellow-500 text-white',
      reviewing: 'bg-blue-500 text-white',
      accepted: 'bg-green-500 text-white',
      scheduled: 'bg-purple-500 text-white',
      admitted: 'bg-teal-600 text-white',
      declined: 'bg-red-500 text-white',
      withdrawn: 'bg-gray-500 text-white'
    };
    return <Badge className={config[status] || config.pending}>{dashboardData?.statuses?.[status]?.label || status}</Badge>;
  };

  const getUrgencyBadge = (urgency) => {
    const config = {
      routine: 'bg-gray-100 text-gray-800',
      urgent: 'bg-orange-100 text-orange-800',
      emergent: 'bg-red-100 text-red-800'
    };
    return <Badge className={config[urgency] || config.routine}>{urgency.toUpperCase()}</Badge>;
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600 font-semibold">{title}</p>
            <p className="text-2xl font-black text-slate-900 mt-1">{value}</p>
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="referral-source-portal">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-black text-slate-900">Referral Source Portal</h1>
              <p className="text-sm text-slate-700 mt-1 font-semibold">Submit and track hospice referrals</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={fetchDashboard}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white font-bold" onClick={() => setShowNewReferral(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Referral
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="font-bold">Dashboard</TabsTrigger>
            <TabsTrigger value="referrals" className="font-bold">My Referrals</TabsTrigger>
            <TabsTrigger value="analytics" className="font-bold">Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <StatCard title="Total Referrals" value={dashboardData?.stats?.total_referrals || 0} icon={FileText} color="blue" />
              <StatCard title="Pending" value={dashboardData?.stats?.pending_review || 0} icon={Clock} color="yellow" />
              <StatCard title="Accepted" value={dashboardData?.stats?.accepted || 0} icon={CheckCircle} color="green" />
              <StatCard title="Avg Response" value={dashboardData?.stats?.avg_response_time || '0h'} icon={Clock} color="purple" />
              <StatCard title="This Month" value={dashboardData?.stats?.this_month || 0} icon={Calendar} color="teal" />
              <StatCard title="Conversion" value={`${dashboardData?.stats?.conversion_rate || 0}%`} icon={TrendingUp} color="green" />
            </div>

            {/* Recent Referrals */}
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-black">Recent Referrals</CardTitle>
                <CardDescription className="text-slate-700 font-semibold">Your most recent hospice referral submissions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="px-4 py-3 text-sm font-black text-slate-900">Referral #</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">Patient</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">Diagnosis</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">Urgency</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">Status</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">Date</th>
                        <th className="px-4 py-3 text-sm font-black text-slate-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData?.recent_referrals?.map((ref) => (
                        <tr key={ref.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-sm font-bold text-slate-900">{ref.referral_number}</td>
                          <td className="px-4 py-3 font-bold text-slate-900">{ref.patient_name}</td>
                          <td className="px-4 py-3 text-slate-800 font-semibold">{ref.diagnosis}</td>
                          <td className="px-4 py-3">{getUrgencyBadge(ref.urgency)}</td>
                          <td className="px-4 py-3">{getStatusBadge(ref.status)}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 font-semibold">{new Date(ref.created_at).toLocaleDateString()}</td>
                          <td className="px-4 py-3">
                            <Button size="sm" variant="outline" onClick={() => fetchReferralDetail(ref.id)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="cursor-pointer hover:shadow-md" onClick={() => setShowNewReferral(true)}>
                <CardContent className="p-6 text-center">
                  <Plus className="h-12 w-12 text-teal-600 mx-auto mb-3" />
                  <h3 className="font-black text-slate-900">Submit New Referral</h3>
                  <p className="text-sm text-slate-700 font-semibold mt-1">Start a new hospice referral</p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md">
                <CardContent className="p-6 text-center">
                  <Phone className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                  <h3 className="font-black text-slate-900">Contact Intake</h3>
                  <p className="text-sm text-slate-700 font-semibold mt-1">24/7 Referral Line: 703-578-7108</p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md">
                <CardContent className="p-6 text-center">
                  <FileText className="h-12 w-12 text-purple-600 mx-auto mb-3" />
                  <h3 className="font-black text-slate-900">Download Forms</h3>
                  <p className="text-sm text-slate-700 font-semibold mt-1">Referral forms and guides</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="referrals" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-black">All Referrals</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.recent_referrals?.map((ref) => (
                    <div key={ref.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border hover:border-teal-300 cursor-pointer" onClick={() => fetchReferralDetail(ref.id)}>
                      <div className="flex items-center gap-4">
                        <div className="p-2 rounded-lg bg-teal-100">
                          <FileText className="h-5 w-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-black text-slate-900">{ref.patient_name}</p>
                          <p className="text-sm text-slate-700 font-semibold">{ref.referral_number} - {ref.diagnosis}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(ref.status)}
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-900 font-black">Referral Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-3xl font-black text-teal-600">{dashboardData?.stats?.conversion_rate || 0}%</p>
                    <p className="text-sm text-slate-700 font-semibold">Conversion Rate</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-3xl font-black text-blue-600">{dashboardData?.stats?.avg_response_time || '0h'}</p>
                    <p className="text-sm text-slate-700 font-semibold">Avg Response Time</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-3xl font-black text-green-600">{dashboardData?.stats?.this_month || 0}</p>
                    <p className="text-sm text-slate-700 font-semibold">This Month</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-3xl font-black text-purple-600">{dashboardData?.stats?.accepted || 0}</p>
                    <p className="text-sm text-slate-700 font-semibold">Accepted</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* New Referral Dialog */}
      <Dialog open={showNewReferral} onOpenChange={setShowNewReferral}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-black">Submit New Referral</DialogTitle>
            <DialogDescription className="text-slate-700 font-semibold">Complete the form to submit a hospice referral</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Patient Info */}
            <div className="border-b pb-4">
              <h4 className="font-black text-slate-900 mb-3">Patient Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-bold text-slate-900">Patient Name *</Label>
                  <Input value={newReferral.patient_name} onChange={(e) => setNewReferral({...newReferral, patient_name: e.target.value})} />
                </div>
                <div>
                  <Label className="font-bold text-slate-900">Date of Birth</Label>
                  <Input type="date" value={newReferral.patient_dob} onChange={(e) => setNewReferral({...newReferral, patient_dob: e.target.value})} />
                </div>
                <div>
                  <Label className="font-bold text-slate-900">Phone</Label>
                  <Input value={newReferral.patient_phone} onChange={(e) => setNewReferral({...newReferral, patient_phone: e.target.value})} />
                </div>
                <div>
                  <Label className="font-bold text-slate-900">Address</Label>
                  <Input value={newReferral.patient_address} onChange={(e) => setNewReferral({...newReferral, patient_address: e.target.value})} />
                </div>
              </div>
            </div>

            {/* Clinical Info */}
            <div className="border-b pb-4">
              <h4 className="font-black text-slate-900 mb-3">Clinical Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-bold text-slate-900">Primary Diagnosis *</Label>
                  <Select value={newReferral.diagnosis} onValueChange={(value) => setNewReferral({...newReferral, diagnosis: value})}>
                    <SelectTrigger><SelectValue placeholder="Select diagnosis" /></SelectTrigger>
                    <SelectContent>
                      {dashboardData?.diagnoses?.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-bold text-slate-900">Urgency</Label>
                  <Select value={newReferral.urgency} onValueChange={(value) => setNewReferral({...newReferral, urgency: value})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="routine">Routine</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="emergent">Emergent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="font-bold text-slate-900">Diagnosis Details</Label>
                  <Textarea value={newReferral.diagnosis_details} onChange={(e) => setNewReferral({...newReferral, diagnosis_details: e.target.value})} />
                </div>
              </div>
            </div>

            {/* Physician Info */}
            <div className="border-b pb-4">
              <h4 className="font-black text-slate-900 mb-3">Referring Physician</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-bold text-slate-900">Physician Name *</Label>
                  <Input value={newReferral.referring_physician} onChange={(e) => setNewReferral({...newReferral, referring_physician: e.target.value})} />
                </div>
                <div>
                  <Label className="font-bold text-slate-900">Phone</Label>
                  <Input value={newReferral.physician_phone} onChange={(e) => setNewReferral({...newReferral, physician_phone: e.target.value})} />
                </div>
                <div>
                  <Label className="font-bold text-slate-900">NPI</Label>
                  <Input value={newReferral.physician_npi} onChange={(e) => setNewReferral({...newReferral, physician_npi: e.target.value})} />
                </div>
                <div>
                  <Label className="font-bold text-slate-900">Referral Source</Label>
                  <Select value={newReferral.referral_source} onValueChange={(value) => setNewReferral({...newReferral, referral_source: value})}>
                    <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                    <SelectContent>
                      {dashboardData?.sources?.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="font-bold text-slate-900">Additional Notes</Label>
              <Textarea value={newReferral.notes} onChange={(e) => setNewReferral({...newReferral, notes: e.target.value})} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewReferral(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white font-bold" onClick={submitReferral}>
              <Send className="h-4 w-4 mr-2" />
              Submit Referral
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Referral Detail Dialog */}
      <Dialog open={!!selectedReferral} onOpenChange={() => setSelectedReferral(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-black">Referral Details</DialogTitle>
            <DialogDescription className="text-slate-700 font-semibold">{selectedReferral?.referral?.referral_number}</DialogDescription>
          </DialogHeader>
          {selectedReferral && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-black text-xl text-slate-900">{selectedReferral.referral.patient_name}</h3>
                  <p className="text-slate-700 font-semibold">{selectedReferral.referral.diagnosis}</p>
                </div>
                {getStatusBadge(selectedReferral.referral.status)}
              </div>

              {/* Timeline */}
              <div className="border-t pt-4">
                <h4 className="font-black text-slate-900 mb-3">Status Timeline</h4>
                <div className="space-y-3">
                  {selectedReferral.timeline?.map((event, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-teal-500" />
                      <div>
                        <p className="font-bold text-slate-900">{event.event}</p>
                        <p className="text-sm text-slate-700">{event.user} - {new Date(event.date).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedReferral(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReferralSourcePortal;
